import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SectionLabel, SectionHeading, SecondaryButton } from './ui/UI';
const audiences = [{
  id: 'saas',
  label: 'SaaS',
  title: 'For SaaS companies',
  sub: 'Enterprise customers increasingly ask for proof.',
  body: 'Periscan helps SaaS companies answer security reviews with validated evidence, not screenshots and guesswork.',
  points: ['Enterprise customer reviews', 'SOC 2 readiness', 'Investor diligence', 'Cloud and identity risk', 'Code and secrets exposure', 'AI app validation', 'Remediation proof', 'Board reporting'],
  cta: 'Get Your First Report'
}, {
  id: 'ai',
  label: 'AI Companies',
  title: 'For AI-native companies',
  sub: 'Your AI app is now part of your attack surface.',
  body: 'Periscan helps AI companies validate AI-system risk and package the results into customer-ready evidence.',
  points: ['Enterprise customer review', 'AI product launch', 'SOC 2 readiness', 'Security questionnaire response', 'Investor diligence', 'Internal risk review', 'Model or prompt update', 'Agent/tool rollout'],
  cta: 'Validate Your AI App'
}, {
  id: 'security',
  label: 'Security Teams',
  title: 'For security teams',
  sub: 'Stop triaging noise.',
  body: 'Periscan helps security teams focus on validated paths, not disconnected findings.',
  points: ['Identify realistic paths to compromise', 'Rank risk by evidence, not severity labels', 'Validate what is reachable or exploitable', 'Generate clear remediation', 'Verify closure', 'Produce proof for leadership', 'Support customer and audit workflows', 'Reduce time wasted on low-context findings'],
  cta: 'Get Early Access'
}, {
  id: 'it',
  label: 'IT Teams',
  title: 'For IT teams',
  sub: 'Security should not require guessing what to fix first.',
  body: 'Periscan translates validation results into clear action.',
  points: ['What is exposed', 'Why it matters', 'What system is involved', 'What fix is recommended', 'Who should own it', 'How to verify closure', 'What proof is needed'],
  cta: 'Get Early Access'
}, {
  id: 'mssp',
  label: 'MSSPs / vCISOs',
  title: 'For MSSPs and vCISOs',
  sub: 'Deliver continuous validation across every client.',
  body: 'Package attack-path validation, AI app security validation, fix verification, and evidence reporting into recurring client services.',
  points: ['Monthly validation reports', 'White-label evidence packs', 'Client risk summaries', 'Security QBR content', 'Remediation plans', 'Fix verification', 'Cyber-insurance readiness', 'AI app validation', 'Multi-client dashboards', 'Technician workflows'],
  cta: 'Join MSSP Early Access'
}, {
  id: 'enterprise',
  label: 'Enterprise',
  title: 'For modern enterprise teams',
  sub: 'Faster, lighter-weight validation — without waiting for enterprise deployment cycles.',
  body: 'Periscan can start self-serve and expand into larger governance, runner, SSO, RBAC, and reporting workflows.',
  points: ['Business unit validation', 'AI app team validation', 'Cloud team self-service', 'Subsidiary assessments', 'M&A security reviews', 'Portfolio company validation', 'Customer-facing evidence', 'Internal risk reviews', 'Pre-audit preparation', 'Remediation verification'],
  cta: 'Contact Enterprise Access'
}];
export function Audiences() {
  const [active, setActive] = useState(0);
  const a = audiences[active];
  return <section className="relative w-full py-32 border-t border-white/5" data-id="element-176">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-177">
        <SectionLabel number="16" label="Who It's For" className="mb-8" data-id="element-178" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12" data-id="element-179">
          <div className="lg:col-span-8" data-id="element-180">
            <SectionHeading size="xl" data-id="element-181">
              Built for teams
              <br data-id="element-182" />
              <span className="text-white/40" data-id="element-183">under proof pressure.</span>
            </SectionHeading>
          </div>
        </div>

        {/* Tab strip */}
        <div className="border-b border-white/5 mb-10 overflow-x-auto no-scrollbar" data-id="element-184">
          <div className="flex gap-1 min-w-max" data-id="element-185">
            {audiences.map((aud, i) => <button key={aud.id} onClick={() => setActive(i)} className={`relative px-5 py-4 text-sm font-medium transition-colors whitespace-nowrap ${active === i ? 'text-white' : 'text-white/40 hover:text-white/70'}`} data-id="element-186">
                {aud.label}
                {active === i && <motion.div layoutId="audience-tab" className="absolute bottom-0 left-0 right-0 h-px bg-[#1a8fd4]" data-id="element-187" />}
              </button>)}
          </div>
        </div>

        {/* Active audience */}
        <AnimatePresence mode="wait" data-id="element-188">
          <motion.div key={a.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.3
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-189">
            <div className="lg:col-span-5" data-id="element-190">
              <h3 className="text-3xl md:text-4xl font-semibold text-white tracking-tight mb-4" data-id="element-191">
                {a.title}
              </h3>
              <p className="text-xl text-[#1a8fd4] font-medium mb-6" data-id="element-192">{a.sub}</p>
              <p className="text-base text-white/60 leading-relaxed mb-8" data-id="element-193">
                {a.body}
              </p>
              <SecondaryButton href="#early-access" size="md" data-id="element-194">
                {a.cta}
              </SecondaryButton>
            </div>

            <div className="lg:col-span-7" data-id="element-195">
              <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-6" data-id="element-196">
                Use Periscan for
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3" data-id="element-197">
                {a.points.map((point, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -5
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.3,
                delay: i * 0.04
              }} className="flex items-center gap-3 py-2 border-b border-white/5" data-id="element-198">
                    <span className="font-mono text-xs text-[#1a8fd4]/60" data-id="element-199">
                      ▸
                    </span>
                    <span className="text-base text-white/80" data-id="element-200">{point}</span>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}